import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import {
  SessionType,
  getBaseUrl,
  logRocketIdentifyAdvisorUser,
  logRocketIdentifyClientUser,
} from '@shared';
import { FullScreenLoadingOverlay } from './FullScreenLoadingOverlay';
import type { ChildrenProps } from '../types/ChildrenProps';
import {
  closeSession,
  fetchSessionForAuthRequest,
  selectSession,
  storeAuth0Session,
} from '../model';
import { Login } from '../pages/Login';
import { DuplicateAccountScreen } from '@client/pages/DuplicateAccountScreen';
import { getPublicRoutePaths } from '../Router';
import { setAuthToken, clearAuthToken } from '@client/api/queryClient';
import { useSignOut } from './hooks/use-sign-out';
import { useClientRoutes } from '../hooks/useClientRoutes';
import { LoginRedirect } from './LoginRedirect';
import { getStoredImpersonationTarget, initImpersonation } from '@client/model/impersonationModel';

export function SessionLoader({ children }: ChildrenProps) {
  const session = useSelector(selectSession);
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, logout } = useAuth0();
  const signOut = useSignOut();
  const { isPublicOnboardingRoute } = useClientRoutes();

  // Check if current path is a public route
  const publicRoutes = getPublicRoutePaths();
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

  const isAuthenticatedAndUserLoaded = isAuthenticated && user && user.sub;
  const isLoadingOrSessionLoading = isLoading || session.isLoading;

  useEffect(() => {
    // load auth0 session
    async function loadAuth0Session() {
      if (!isAuthenticatedAndUserLoaded) {
        clearAuthToken();
        dispatch(closeSession());
        return;
      }
      try {
        const authUserId = user.sub!;
        const accessToken = await getAccessTokenSilently();
        setAuthToken(accessToken);
        dispatch(
          storeAuth0Session({
            authUserId,
            accessToken,
          }),
        );

        // Restore impersonation Redux state from localStorage if it was active before page reload
        const storedTarget = getStoredImpersonationTarget();
        if (storedTarget) {
          dispatch(initImpersonation(storedTarget));
        }

        dispatch(fetchSessionForAuthRequest());
      } catch (error) {
        clearAuthToken();
        dispatch(closeSession());
      }
    }
    if (session.sessionType === SessionType.unknown && !isLoadingOrSessionLoading) {
      loadAuth0Session();
    }
  }, [dispatch, session.sessionType, isAuthenticated, isLoading, user, session.isLoading]);

  // identify user in logrocket
  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Handle client users
    if (session.sessionType === SessionType.client && isAuthenticated) {
      const { auth0Session } = session;
      const auth0UserId = auth0Session?.authUserId;
      const clientEmail = auth0Session?.clientUser?.email;
      if (auth0UserId && clientEmail) {
        logRocketIdentifyClientUser(auth0UserId, clientEmail);
      }
      return;
    }

    // Handle advisor users
    if (session?.advisor?.advisorId) {
      const advisorId = session.advisor.advisorId;
      logRocketIdentifyAdvisorUser(advisorId, session.advisor.advisorFirstName);
      return;
    }
  }, [session.sessionType, session.advisor, session.auth0Session, isAuthenticated, isLoading]);

  // Logout from Auth0 when session is expired.
  // Guard against triggering during session initialization to prevent race condition:
  // After signup redirect, Auth0 reports isAuthenticated=true but the session API call
  // may still be in progress or may have transiently failed. Only trigger signOut when
  // we're certain the session has genuinely expired (not during initial load).
  const isSessionInitializing = session.isLoading || session.isInvalidating;
  useEffect(() => {
    if (
      session.sessionType === SessionType.unauthenticated &&
      isAuthenticated &&
      !isLoading &&
      !isPublicOnboardingRoute &&
      !isSessionInitializing
    ) {
      // When in a client sign-in flow, preserve the client_sign_in params during logout.
      // Without this, the stale Auth0 session gets cleared but the user lands on the advisor
      // login page (/) instead of being redirected back to the client sign-in flow.
      const searchParams = new URLSearchParams(location.search);
      const clientSignIn = searchParams.get('client_sign_in');

      if (clientSignIn === 'true') {
        const loginHint = searchParams.get('login_hint');
        const loginParams = new URLSearchParams();
        loginParams.set('client_sign_in', 'true');
        if (loginHint) loginParams.set('login_hint', loginHint);
        loginParams.set('redirect_to', '/client/home');

        dispatch(closeSession());
        logout({
          logoutParams: {
            returnTo: `${getBaseUrl()}/?${loginParams.toString()}`,
          },
        });
        return;
      }

      signOut();
    }
  }, [
    session.sessionType,
    isAuthenticated,
    isLoading,
    signOut,
    isPublicOnboardingRoute,
    isSessionInitializing,
    dispatch,
    logout,
  ]);

  if (session.sessionType === SessionType.unknown || isLoadingOrSessionLoading) {
    if (isPublicOnboardingRoute) {
      return children;
    }
    return <FullScreenLoadingOverlay isLoading={true} />;
  }

  if (session.sessionType === SessionType.duplicateAccount) {
    return <DuplicateAccountScreen />;
  }

  // Unauthenticated gate - the single auth decision point
  if (!isAuthenticatedAndUserLoaded || session.sessionType === SessionType.unauthenticated) {
    // Public routes (signup, specs, etc.) render without auth
    if (isPublicRoute || isPublicOnboardingRoute) {
      return children;
    }
    // If already on login page, render Login directly
    if (location.pathname === '/') {
      return <Login />;
    }
    // Otherwise redirect to login with preserved params
    return <LoginRedirect />;
  }

  return session.sessionType !== SessionType.unknown ? children : null;
}
